import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@cca/ui';
import { apiRequest, ApiError } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface EvidenceItem {
  readonly label: string;
  readonly value: string;
  readonly source: string;
  readonly observedAt?: string | null;
}

interface OrderException {
  readonly id: string;
  readonly orderName: string;
  readonly customerEmail: string | null;
  readonly type: string;
  readonly severity: 'LOW' | 'MEDIUM' | 'HIGH';
  readonly detectedAt: string;
  readonly expectedDate: string | null;
  readonly currentStatus: string;
  readonly calculationMethod: string;
  readonly recommendedAction: string;
  readonly evidence: readonly EvidenceItem[];
  readonly resolution: string;
}

const SEVERITY_TONE = { HIGH: 'danger', MEDIUM: 'warning', LOW: 'neutral' } as const;

function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/**
 * The evidence panel is the point of this screen. An exception that says "this
 * order is late" without showing the arithmetic is asking the merchant to take
 * our word for it; showing the window, the calendar and the evaluation date lets
 * them check.
 */
function EvidencePanel({ exception }: { readonly exception: OrderException }) {
  return (
    <div className="mt-3 rounded-md bg-slate-50 p-3 dark:bg-slate-950">
      <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Why this was flagged</p>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
        {exception.calculationMethod}
      </p>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        {exception.evidence.map((item, index) => (
          <div key={`${item.label}-${index}`} className="flex justify-between gap-3 text-xs">
            <dt className="text-slate-500 dark:text-slate-400">{item.label}</dt>
            <dd className="text-right font-medium text-slate-800 dark:text-slate-200">
              {item.value}
              <span className="ml-1 font-normal text-slate-400">({item.source})</span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ExceptionsPage() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [severity, setSeverity] = useState<'ALL' | 'HIGH' | 'MEDIUM' | 'LOW'>('ALL');

  const exceptions = useQuery({
    queryKey: ['exceptions', severity],
    enabled: Boolean(accessToken),
    queryFn: () =>
      apiRequest<{ exceptions: OrderException[]; total: number }>(
        `/api/exceptions?limit=100${severity === 'ALL' ? '' : `&severity=${severity}`}`,
        { accessToken },
      ),
  });

  const detect = useMutation({
    mutationFn: () =>
      apiRequest<{ ordersScanned: number; exceptionsCreated: number }>('/api/exceptions/detect', {
        method: 'POST',
        accessToken,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exceptions'] }),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Order Exceptions
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Orders and shipments that need attention, with the evidence behind each verdict.
          </p>
        </div>
        <Button onClick={() => detect.mutate()} disabled={detect.isPending} variant="outline">
          {detect.isPending ? 'Scanning…' : 'Run detection now'}
        </Button>
      </header>

      {detect.isSuccess ? (
        <div
          role="status"
          className="rounded-md bg-sky-50 p-3 text-sm text-sky-900 dark:bg-sky-950 dark:text-sky-200"
        >
          Scanned {detect.data.ordersScanned} order(s); created {detect.data.exceptionsCreated} new
          exception(s).
        </div>
      ) : null}

      {detect.isError ? (
        <ErrorState
          title="Detection failed"
          description={
            detect.error instanceof ApiError ? detect.error.message : 'Unexpected error.'
          }
          onRetry={() => detect.mutate()}
        />
      ) : null}

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by severity">
        {(['ALL', 'HIGH', 'MEDIUM', 'LOW'] as const).map((level) => (
          <Button
            key={level}
            size="sm"
            variant={severity === level ? 'primary' : 'outline'}
            aria-pressed={severity === level}
            onClick={() => setSeverity(level)}
          >
            {level}
          </Button>
        ))}
      </div>

      {exceptions.isPending ? <LoadingState label="Loading exceptions" /> : null}

      {exceptions.isError ? (
        <ErrorState
          description={
            exceptions.error instanceof ApiError
              ? exceptions.error.message
              : 'Could not load exceptions.'
          }
          onRetry={() => void exceptions.refetch()}
        />
      ) : null}

      {exceptions.data && exceptions.data.exceptions.length === 0 ? (
        <Card>
          <EmptyState
            title="No open exceptions"
            description="Nothing currently needs attention. Run detection to re-scan orders."
          />
        </Card>
      ) : null}

      <ul className="space-y-3">
        {(exceptions.data?.exceptions ?? []).map((exception) => (
          <li key={exception.id}>
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle>
                    {exception.orderName} — {exception.type.replaceAll('_', ' ').toLowerCase()}
                  </CardTitle>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Detected {daysSince(exception.detectedAt)}d ago · status{' '}
                    {exception.currentStatus}
                    {exception.expectedDate ? ` · expected by ${exception.expectedDate}` : ''}
                    {exception.customerEmail ? ` · ${exception.customerEmail}` : ''}
                  </p>
                </div>
                <Badge tone={SEVERITY_TONE[exception.severity]}>{exception.severity}</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  <span className="font-medium">Recommended: </span>
                  {exception.recommendedAction}
                </p>

                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 px-0"
                  aria-expanded={expanded === exception.id}
                  onClick={() => setExpanded(expanded === exception.id ? null : exception.id)}
                >
                  {expanded === exception.id ? 'Hide evidence' : 'Show evidence'}
                </Button>

                {expanded === exception.id ? <EvidencePanel exception={exception} /> : null}
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const exceptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/exceptions',
  component: () => (
    <RequireSession>
      <ExceptionsPage />
    </RequireSession>
  ),
});
